import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Palette, Loader2, ScanSearch, Eraser, AlertTriangle, RefreshCw, Check, X, Undo2, Sparkles, Wand2, Send, SwatchBook } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { toast } from '@/components/ui/sonner';
import {
  adminColorOptions, adminColorProfiles, adminColorCreateProfile, adminColorProject, adminColorUpdateProject,
  adminColorAnalyze, adminColorClearAnalysis, adminColorSetDecisions, adminColorBulkDecisions,
  adminColorEstimate, adminColorConfirmScenes, adminColorApplyApproved, adminColorRevertAll,
} from '@/lib/api';
import { COLOR } from '@/constants/testIds';
import { DirectionPanel } from '@/components/color/DirectionPanel';
import { ConsistencyPanel } from '@/components/color/ConsistencyPanel';
import { ColorGrid } from '@/components/color/ColorGrid';
import { ImageDetailSheet } from '@/components/color/ImageDetailSheet';
import { FILTERS, matchesFilter, movesOf } from '@/components/color/colorUtils';
import { readBoolPref, writeBoolPref } from '@/lib/prefs';
const AI_SCENE_PREF_KEY = 'lxra.color.aiScene';

export default function ColorStudio() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [options, setOptions] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(() => new Set());
  const [openId, setOpenId] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await adminColorProject(slug);
      setData(d);
      setError(null);
      return d;
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load the Color Studio.');
      return null;
    }
  }, [slug]);

  useEffect(() => {
    load();
    adminColorOptions().then(setOptions).catch(() => {});
    adminColorProfiles().then(setProfiles).catch(() => {});
  }, [load]);

  const job = data?.project?.job;
  const running = job?.status === 'running';
  const [aiScene, setAiScene] = useState(() => readBoolPref(AI_SCENE_PREF_KEY, false));
  const [estimate, setEstimate] = useState(null);
  useEffect(() => { adminColorEstimate(slug).then(setEstimate).catch(() => setEstimate(null)); }, [slug, data?.stats?.images]);
  const toggleAiScene = (v) => { setAiScene(v); writeBoolPref(AI_SCENE_PREF_KEY, v); };
  useEffect(() => {
    if (!running) { if (pollRef.current) clearInterval(pollRef.current); pollRef.current = null; return undefined; }
    pollRef.current = setInterval(async () => {
      const d = await load();
      if (d && d.project?.job?.status !== 'running') {
        clearInterval(pollRef.current); pollRef.current = null;
        if (d.project.job?.status === 'done') toast.success(`Analysed ${d.stats.analyzed} photo${d.stats.analyzed === 1 ? '' : 's'} — consistency ${d.project.consistency?.score ?? '—'}%.`);
        else toast.error(d.project.job?.error || 'The analysis failed.');
      }
    }, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [running, load]);

  const images = useMemo(() => data?.images || [], [data?.images]);
  const visible = useMemo(() => images.filter((i) => matchesFilter(i, filter)), [images, filter]);
  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.key, images.filter((i) => matchesFilter(i, f.key)).length])), [images]);
  const openIndex = openId ? visible.findIndex((i) => i.id === openId) : -1;
  const openImage = openIndex >= 0 ? visible[openIndex] : null;

  const updateProject = async (patch) => {
    setBusy(true);
    try { setData(await adminColorUpdateProject(slug, patch)); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not update the project.'); }
    finally { setBusy(false); }
  };

  const analyze = async (force = false) => {
    setBusy(true);
    try {
      await adminColorAnalyze(slug, { force, ai_scene: aiScene });
      toast.info(`${force ? 'Re-analysing every photo' : 'Analysing new and changed photos'}${aiScene ? ' + AI scene confirmation' : ''}…`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start the analysis.'); }
    finally { setBusy(false); }
  };

  const confirmScenes = async () => {
    setBusy(true);
    try { await adminColorConfirmScenes(slug, {}); toast.info(`Confirming scenes with the vision model · ~${estimate?.ai_calls ?? '?'} calls…`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not start scene confirmation.'); }
    finally { setBusy(false); }
  };

  const applyApproved = async () => {
    setBusy(true);
    try { const j = await adminColorApplyApproved(slug, {}); toast.info(`Rendering graded copies for ${j.total} photo${j.total === 1 ? '' : 's'}…`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Nothing to apply.'); }
    finally { setBusy(false); }
  };

  const revertAll = async () => {
    setBusy(true);
    try { const r = await adminColorRevertAll(slug); toast.success(`Removed ${r.reverted} graded cop${r.reverted === 1 ? 'y' : 'ies'}. Originals were never changed.`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not revert.'); }
    finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true);
    try { await adminColorClearAnalysis(slug); toast.success('Analysis cleared. Originals were never touched.'); setSelected(new Set()); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not clear.'); }
    finally { setBusy(false); }
  };

  const saveStyle = async (name) => {
    try {
      const p = await adminColorCreateProfile({ name, duplicate_of: data.project.profile_id, style: data.project.direction?.style || 'Custom', mood: data.project.direction?.mood || 'Neutral', preferences: data.project.direction?.preferences || [], params: data.effective_params, skin: data.profile?.skin || 'natural', description: `Saved from ${data.gallery.name}` });
      setProfiles(await adminColorProfiles());
      await updateProject({ profile_id: p.id });
      toast.success(`“${name}” saved to My Styles and applied.`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save the profile.'); }
  };

  const decide = async (param, decision) => {
    if (!openImage) return;
    try {
      const r = await adminColorSetDecisions(slug, openImage.id, { decisions: { [param]: decision } });
      setData((d) => ({ ...d, images: d.images.map((i) => (i.id === openImage.id ? { ...i, color: { ...i.color, decisions: r.decisions } } : i)) }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save the decision.'); }
  };

  const decideAll = async (state) => {
    if (!openImage) return;
    const params = movesOf(openImage).map((m) => m.param);
    if (!params.length) return;
    try {
      const r = await adminColorSetDecisions(slug, openImage.id, { decisions: Object.fromEntries(params.map((p) => [p, { state }])) });
      setData((d) => ({ ...d, images: d.images.map((i) => (i.id === openImage.id ? { ...i, color: { ...i.color, decisions: r.decisions } } : i)) }));
      toast.success(state === 'accepted' ? 'All recommendations accepted for this photo.' : 'All recommendations rejected for this photo.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save.'); }
  };

  const bulk = async (state) => {
    if (!selected.size) return;
    setBusy(true);
    try {
      const r = await adminColorBulkDecisions(slug, { image_ids: [...selected], state });
      toast.success(state === 'clear' ? `Decisions cleared on ${r.updated} photo${r.updated === 1 ? '' : 's'}.` : `${state === 'accepted' ? 'Accepted' : 'Rejected'} recommendations on ${r.updated} photo${r.updated === 1 ? '' : 's'}.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk action failed.'); }
    finally { setBusy(false); }
  };

  const setReference = async (imageId, on) => {
    const cur = data.project.reference_image_ids || [];
    const next = on ? [...new Set([...cur, imageId])].slice(-5) : cur.filter((id) => id !== imageId);
    await updateProject({ reference_image_ids: next });
    toast.success(on ? 'Reference set — the whole set now targets this frame’s balance.' : 'Reference removed.');
  };

  const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  if (error) {
    return (
      <div data-testid={COLOR.root} className="min-h-screen bg-[#0A0A0A] p-6 text-[hsl(var(--lxra-fg))]">
        <Link to="/admin" data-testid={COLOR.backLink} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))]"><ArrowLeft className="h-3.5 w-3.5" /> Studio Dashboard</Link>
        <div className="mt-6 flex items-start gap-3 rounded-2xl border border-red-400/30 bg-red-950/20 p-5 text-sm text-red-200"><AlertTriangle className="h-4 w-4 mt-0.5" /> {error} <Button variant="ghost" size="sm" onClick={load} className="ml-auto h-7 text-xs text-red-100"><RefreshCw className="mr-1 h-3 w-3" /> Retry</Button></div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-[#0A0A0A] p-6"><Skeleton className="h-9 w-72 bg-[#141416]" /><Skeleton className="mt-6 h-56 w-full bg-[#141416] rounded-2xl" /><Skeleton className="mt-4 h-40 w-full bg-[#141416] rounded-2xl" /></div>;
  }

  const analyzed = data.stats.analyzed;
  const pct = job?.total ? Math.round((job.done / job.total) * 100) : 0;

  return (
    <div data-testid={COLOR.root} className="min-h-screen bg-[#0A0A0A] text-[hsl(var(--lxra-fg))]">
      <div className="mx-auto max-w-7xl px-4 pb-32 pt-6 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <Link to="/admin" data-testid={COLOR.backLink} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))]"><ArrowLeft className="h-3.5 w-3.5" /> Studio Dashboard</Link>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <h1 data-testid={COLOR.title} className="font-display text-4xl tracking-[0.03em] sm:text-5xl">Color Studio <span className="text-[hsl(var(--lxra-gold))]">·</span> {data.gallery.name}</h1>
              <span data-testid={COLOR.statusBadge} className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] ${running ? 'border-[hsl(var(--lxra-gold))]/60 text-[hsl(var(--lxra-gold))]' : analyzed ? 'border-emerald-400/40 text-emerald-300' : 'border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))]'}`}>{running ? (job.kind === 'apply' ? 'rendering' : job.stage === 'ai' ? 'confirming scenes' : 'analysing') : analyzed ? `${analyzed}/${data.stats.images} analysed` : 'not analysed'}</span>
              <Link to="/admin/color/styles" data-testid={COLOR.styleLibraryLink} className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--lxra-border))] px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))]"><SwatchBook className="h-3 w-3" /> Style Library</Link>
            </div>
            <p className="mt-1 max-w-2xl text-sm text-[hsl(var(--lxra-muted))]"><Palette className="mr-1 inline h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> Colour Intelligence measures every frame, compares it with your direction and reference, and proposes moves you accept, modify or reject. Originals are never altered.</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="ai-scene" className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--lxra-fg))]"><Sparkles className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> AI scene confirmation</Label>
              <Switch id="ai-scene" data-testid={COLOR.aiSceneSwitch} checked={aiScene} onCheckedChange={toggleAiScene} disabled={running || !estimate?.ai_configured} className="data-[state=checked]:bg-[hsl(var(--lxra-gold))]" />
              <span data-testid={COLOR.aiEstimate} className="text-[10px] text-[hsl(var(--lxra-muted))]">{aiScene && estimate?.ai_configured ? `~${estimate.ai_calls} call${estimate.ai_calls === 1 ? '' : 's'} · ${estimate.model}` : 'no AI credits'}</span>
            </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {analyzed > 0 && !running && estimate?.ai_configured && <Button variant="outline" size="sm" data-testid={COLOR.confirmScenesButton} onClick={confirmScenes} disabled={busy} className="h-9 border-[hsl(var(--lxra-border))] bg-transparent text-xs uppercase tracking-[0.08em] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))]"><Sparkles className="mr-1.5 h-3.5 w-3.5" /> Confirm scenes</Button>}
            {analyzed > 0 && !running && <Button variant="ghost" size="sm" data-testid={COLOR.clearButton} onClick={clear} disabled={busy} className="h-9 text-xs uppercase tracking-[0.08em] text-[hsl(var(--lxra-muted))] hover:bg-[#1a1a1c] hover:text-[hsl(var(--lxra-fg))]"><Eraser className="mr-1.5 h-3.5 w-3.5" /> Clear</Button>}
            {analyzed > 0 && !running && <Button variant="outline" size="sm" data-testid={COLOR.reanalyzeButton} onClick={() => analyze(true)} disabled={busy} className="h-9 border-[hsl(var(--lxra-border))] bg-transparent text-xs uppercase tracking-[0.08em] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))]"><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Re-analyse all</Button>}
            <Button data-testid={COLOR.analyzeButton} onClick={() => analyze(false)} disabled={busy || running || data.stats.images === 0} className="h-9 bg-[hsl(var(--lxra-gold))] px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))]">
              {running ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <ScanSearch className="mr-1.5 h-3.5 w-3.5" />}
              {running ? 'Working…' : analyzed ? 'Analyse new photos' : 'Analyse gallery'}
            </Button>
          </div>
          </div>
        </div>

        {analyzed > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] px-4 py-3">
            <div className="flex flex-wrap items-center gap-1.5">
              {[['approved', 'approved', data.stats.approved], ['applied', 'graded copies', data.stats.applied], ['pending', 'pending render', data.stats.pending_apply]].map(([k, label, v]) => (
                <span key={k} data-testid={COLOR.statChip(k)} className={`rounded-md border px-2 py-1 text-[11px] ${k === 'pending' && v > 0 ? 'border-[hsl(var(--lxra-gold))]/50 text-[hsl(var(--lxra-gold))]' : 'border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))]'}`}><span className="font-mono text-[hsl(var(--lxra-fg))]">{v}</span> {label}</span>
              ))}
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2 rounded-lg border border-[hsl(var(--lxra-border))] bg-[#141416] px-2.5 py-1.5">
                <Label htmlFor="deliver-graded" className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--lxra-fg))]"><Send className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> Deliver graded versions to clients</Label>
                <Switch id="deliver-graded" data-testid={COLOR.deliverSwitch} checked={Boolean(data.project.deliver_graded)} onCheckedChange={(v) => updateProject({ deliver_graded: v })} disabled={busy || running} className="data-[state=checked]:bg-[hsl(var(--lxra-gold))]" />
              </div>
              {data.stats.applied > 0 && !running && <Button variant="ghost" size="sm" data-testid={COLOR.revertAllButton} onClick={revertAll} disabled={busy} className="h-9 text-xs uppercase tracking-[0.08em] text-[hsl(var(--lxra-muted))] hover:bg-[#1a1a1c] hover:text-red-300"><Undo2 className="mr-1.5 h-3.5 w-3.5" /> Revert all</Button>}
              <Button size="sm" data-testid={COLOR.applyApprovedButton} onClick={applyApproved} disabled={busy || running || data.stats.pending_apply === 0} className="h-9 bg-[hsl(var(--lxra-gold))] px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] disabled:opacity-40"><Wand2 className="mr-1.5 h-3.5 w-3.5" /> Render approved ({data.stats.pending_apply})</Button>
            </div>
          </div>
        )}

        {running && (
          <div data-testid={COLOR.progress} className="mt-4 space-y-1.5">
            <Progress value={job.stage === 'ai' ? (job.ai_total ? Math.round((job.ai_done / job.ai_total) * 100) : 5) : job.stage === 'recommending' ? 100 : pct} className="h-1.5 bg-[#1a1a1c] [&>div]:bg-[hsl(var(--lxra-gold))]" />
            <p data-testid={COLOR.progressLabel} className="text-[11px] text-[hsl(var(--lxra-muted))]">
              {job.kind === 'apply' ? `Rendering graded copies · ${job.done}/${job.total}${job.failed ? ` · ${job.failed} failed` : ''} · non-destructive, originals untouched`
                : job.stage === 'ai' ? `Confirming lighting scenes with the vision model · batch ${job.ai_done}/${job.ai_total} · 4 photos per call`
                : job.stage === 'recommending' ? 'Building recommendations & consistency…'
                : job.stage === 'queued' ? 'Starting…'
                : `Measuring colour & tone · ${job.done}/${job.total}${job.skipped ? ` (${job.skipped} unchanged skipped)` : ''} · free local analysis, no AI credits`}
            </p>
          </div>
        )}
        {job?.ai_error && !running && <p className="mt-4 flex items-center gap-2 rounded-xl border border-amber-300/30 bg-amber-950/20 px-3 py-2 text-xs text-amber-200"><AlertTriangle className="h-3.5 w-3.5" /> {job.ai_error}</p>}
        {job?.status === 'error' && <p data-testid={COLOR.jobError} className="mt-4 flex items-center gap-2 rounded-xl border border-red-400/30 bg-red-950/30 px-3 py-2 text-xs text-red-200"><AlertTriangle className="h-3.5 w-3.5" /> {job.error}</p>}

        <div className="mt-6 grid gap-4 xl:grid-cols-[1.6fr_1fr] xl:items-start">
          <DirectionPanel data={data} options={options} profiles={profiles} onUpdate={updateProject} onSaveStyle={saveStyle} busy={busy || running} />
          <ConsistencyPanel consistency={data.project.consistency} onSelectIssue={(iss) => { setSelected(new Set(iss.image_ids)); setFilter('all'); toast.info(`${iss.count} photo${iss.count === 1 ? '' : 's'} selected — accept or reject their moves below.`); }} />
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-1.5">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" data-testid={COLOR.filter(f.key)} aria-pressed={filter === f.key} onClick={() => setFilter(f.key)} className={`rounded-full border px-3 py-1 text-[11px] uppercase tracking-[0.08em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--lxra-gold))] ${filter === f.key ? 'border-[hsl(var(--lxra-gold))] bg-[hsl(var(--lxra-gold))] text-[#0A0A0A]' : 'border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))] hover:border-[hsl(var(--lxra-gold))]/50 hover:text-[hsl(var(--lxra-fg))]'}`}>
              {f.label} <span className="font-mono opacity-70">{counts[f.key]}</span>
            </button>
          ))}
          {visible.length > 0 && <Button variant="ghost" size="sm" onClick={() => setSelected(selected.size === visible.length ? new Set() : new Set(visible.map((i) => i.id)))} className="ml-auto h-7 text-[11px] uppercase tracking-[0.08em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))]">{selected.size === visible.length ? 'Deselect shown' : 'Select shown'}</Button>}
        </div>

        <div className="mt-4">
          {images.length === 0 ? (
            <div data-testid={COLOR.empty} className="rounded-2xl border border-dashed border-[hsl(var(--lxra-border))] p-10 text-center text-sm text-[hsl(var(--lxra-muted))]">This gallery has no photos yet. Upload photos from the Studio Dashboard, then analyse them here.</div>
          ) : visible.length === 0 ? (
            <div data-testid={COLOR.noResults} className="rounded-2xl border border-dashed border-[hsl(var(--lxra-border))] p-10 text-center text-sm text-[hsl(var(--lxra-muted))]">Nothing matches this filter.<div className="mt-3"><Button variant="outline" size="sm" onClick={() => setFilter('all')} className="border-[hsl(var(--lxra-border))] bg-transparent text-xs text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))]">Show all photos</Button></div></div>
          ) : (
            <ColorGrid images={visible} selectedIds={selected} onToggle={toggle} onOpen={setOpenId} />
          )}
        </div>
      </div>

      {selected.size > 0 && (
        <div data-testid={COLOR.selectionBar} className="fixed inset-x-0 bottom-0 z-40 border-t border-[hsl(var(--lxra-border))] bg-[#0A0A0A]/90 backdrop-blur-xl">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2 px-4 py-3 sm:px-6">
            <span data-testid={COLOR.selectionCount} className="mr-2 text-xs text-[hsl(var(--lxra-fg))]"><span className="font-mono text-[hsl(var(--lxra-gold))]">{selected.size}</span> selected</span>
            <Button size="sm" data-testid={COLOR.bulkAccept} onClick={() => bulk('accepted')} disabled={busy} className="h-8 bg-emerald-600/90 text-[11px] uppercase tracking-[0.08em] text-white hover:bg-emerald-500"><Check className="mr-1.5 h-3.5 w-3.5" /> Accept moves</Button>
            <Button size="sm" variant="outline" data-testid={COLOR.bulkReject} onClick={() => bulk('rejected')} disabled={busy} className="h-8 border-red-400/40 bg-transparent text-[11px] uppercase tracking-[0.08em] text-red-200 hover:bg-red-950/40"><X className="mr-1.5 h-3.5 w-3.5" /> Reject moves</Button>
            <Button size="sm" variant="ghost" data-testid={COLOR.bulkClear} onClick={() => bulk('clear')} disabled={busy} className="h-8 text-[11px] uppercase tracking-[0.08em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-fg))]"><Undo2 className="mr-1.5 h-3.5 w-3.5" /> Clear decisions</Button>
            <Button size="sm" variant="ghost" data-testid={COLOR.selectionClear} onClick={() => setSelected(new Set())} className="ml-auto h-8 text-[11px] uppercase tracking-[0.08em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-fg))]">Deselect</Button>
          </div>
        </div>
      )}

      <ImageDetailSheet
        image={openImage}
        index={openIndex}
        total={visible.length}
        options={options}
        busy={busy}
        onClose={() => setOpenId(null)}
        onDecide={decide}
        onDecideAll={decideAll}
        onSetReference={setReference}
        slug={slug}
        onChanged={load}
        onPrev={() => openIndex > 0 && setOpenId(visible[openIndex - 1].id)}
        onNext={() => openIndex < visible.length - 1 && setOpenId(visible[openIndex + 1].id)}
      />
    </div>
  );
}
